import { useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Loader2, Youtube, Video as VideoIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useLinkPreview, useAddLink, type LinkPreview } from "@/hooks/use-links";
import { isApiError } from "@/lib/api";

type Props = {
  /** "/app/projects" or "/admin/projects" — where to land after adding. */
  projectsBasePath: string;
};

function fmtDuration(s: number | null): string | null {
  if (s == null || !Number.isFinite(s)) return null;
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, "0")}`;
}

/**
 * Add-from-link page (migration 0024). Separate from the file-upload page.
 * Flow: paste URL → Preview (resolve title/thumb via oEmbed) → confirm Add.
 * Project/folder scope arrives in the query string (?projectId=&folderId=),
 * mirroring the upload page's uploadHref.
 */
export function AddLinkPage({ projectsBasePath }: Props) {
  const [params] = useSearchParams();
  const projectId = params.get("projectId") ?? "";
  const folderId = params.get("folderId");
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [url, setUrl] = useState("");
  const [preview, setPreview] = useState<LinkPreview | null>(null);
  const [error, setError] = useState<string | null>(null);

  const previewMut = useLinkPreview();
  const addMut = useAddLink();

  const backHref = folderId
    ? `${projectsBasePath}/${projectId}/folders/${folderId}`
    : `${projectsBasePath}/${projectId}`;

  function onPreview(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setPreview(null);
    const trimmed = url.trim();
    if (!trimmed) return;
    previewMut.mutate(
      { url: trimmed },
      {
        onSuccess: (data) => setPreview(data),
        onError: (err) => setError(isApiError(err) ? err.message : "Could not read that link."),
      },
    );
  }

  function onAdd() {
    if (!preview || !projectId) return;
    setError(null);
    addMut.mutate(
      { projectId, url: preview.url, folderId },
      {
        onSuccess: () => {
          qc.invalidateQueries({ queryKey: ["projects", projectId, "videos"] });
          if (folderId) qc.invalidateQueries({ queryKey: ["folders", folderId, "contents"] });
          qc.invalidateQueries({ queryKey: ["projects"] });
          navigate(backHref);
        },
        onError: (err) => setError(isApiError(err) ? err.message : "Could not add the link."),
      },
    );
  }

  const duration = preview ? fmtDuration(preview.durationSeconds) : null;

  return (
    <div className="mx-auto max-w-3xl px-4 py-6 md:px-8 md:py-10">
      <Link
        to={backHref}
        className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        Back
      </Link>

      <h1 className="font-display text-3xl font-bold tracking-tight">Add from link</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        Paste a YouTube or Vimeo link to review it — comments and drawings, no upload.
      </p>

      {!projectId && (
        <div className="mt-6 rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          No project selected. Open a project and use the “+” menu → Add from link.
        </div>
      )}

      <form onSubmit={onPreview} className="mt-6 flex flex-col gap-2 sm:flex-row">
        <Input
          type="url"
          inputMode="url"
          autoFocus
          value={url}
          onChange={(e) => {
            setUrl(e.target.value);
            if (preview) setPreview(null);
          }}
          placeholder="https://youtube.com/watch?v=…  or  https://vimeo.com/…"
          className="flex-1"
        />
        <Button type="submit" disabled={!url.trim() || previewMut.isPending}>
          {previewMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Preview"}
        </Button>
      </form>

      {error && (
        <div className="mt-4 rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {preview && (
        <div className="mt-6 overflow-hidden rounded-xl border border-border bg-card">
          <div className="flex gap-4 p-4">
            <div className="relative aspect-video w-48 shrink-0 overflow-hidden rounded-lg bg-secondary">
              {preview.thumbnailUrl ? (
                <img src={preview.thumbnailUrl} alt="" className="h-full w-full object-cover" />
              ) : (
                <div className="flex h-full w-full items-center justify-center text-muted-foreground">
                  <VideoIcon className="h-6 w-6" />
                </div>
              )}
              {duration && (
                <span className="absolute bottom-1 right-1 rounded bg-black/80 px-1.5 py-0.5 text-[11px] font-medium text-white">
                  {duration}
                </span>
              )}
            </div>
            <div className="flex min-w-0 flex-col justify-between py-0.5">
              <div>
                <div className="mb-1 inline-flex items-center gap-1.5 rounded-full border border-border px-2 py-0.5 text-[11px] text-muted-foreground">
                  {preview.provider === "youtube" ? (
                    <Youtube className="h-3.5 w-3.5" />
                  ) : (
                    <VideoIcon className="h-3.5 w-3.5" />
                  )}
                  {preview.provider === "youtube" ? "YouTube" : "Vimeo"}
                </div>
                <h2 className="line-clamp-2 text-sm font-semibold text-foreground">{preview.title}</h2>
              </div>
              <div className="mt-3 flex items-center gap-2">
                <Button onClick={onAdd} disabled={addMut.isPending || !projectId}>
                  {addMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add to project"}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setPreview(null);
                    setUrl("");
                  }}
                  disabled={addMut.isPending}
                >
                  Try another
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
